import { Check, MessageCircle } from "lucide-react";
import { Reveal, SectionBadge } from "./Shared";
import { waLink } from "../data";

const PLANS = [
  {
    id: "weekly",
    name: "Weekly Squeeze",
    price: "$60",
    per: "/ week",
    tagline: "Your weekly dose of fresh.",
    features: [
      "6 × 12oz cold-pressed juices",
      "Choose your favorite flavors",
      "Fresh bundle every week",
      "Save $6 on every bundle",
      "Change your flavor selections before your weekly cutoff",
      "Pickup or delivery available",
    ],
    button: "Join Weekly",
    testid: "join-weekly-button",
    msg: "Hi Main Squeeze! I'd like to join the Weekly Squeeze plan.",
  },
  {
    id: "bi-weekly",
    name: "Bi-Weekly Squeeze",
    price: "$60",
    per: "/ every 2 weeks",
    tagline: "A little more flexibility, with all the freshness.",
    features: [
      "6 × 12oz cold-pressed juices",
      "Choose your favorite flavors",
      "Fresh bundle every other week",
      "Save $6 on every bundle",
      "Change your flavor selections before your cutoff",
      "Pickup or delivery available",
    ],
    button: "Join Bi-Weekly",
    testid: "join-biweekly-button",
    msg: "Hi Main Squeeze! I'd like to join the Bi-Weekly Squeeze plan.",
  },
];

const STEPS = [
  ["Pick your plan", "Choose Weekly Squeeze or Bi-Weekly Squeeze."],
  ["Choose your six", "Build your bundle with six of your favorite Main Squeeze juices."],
  ["Set your squeeze schedule", "We'll prepare your bundle according to your selected schedule."],
  ["Sip & repeat", "Keep your usual favorites or switch up your flavors before your next order."],
];

export default function SqueezeClub() {
  return (
    <section id="club" data-testid="squeeze-club-section" className="bg-leaf py-24 text-cream md:py-32">
      <div className="mx-auto max-w-[1440px] px-6 md:px-10">
        <Reveal>
          <SectionBadge light>06 — The Squeeze Club</SectionBadge>
        </Reveal>
        <div className="mt-8 flex flex-wrap items-end justify-between gap-8">
          <Reveal delay={0.1}>
            <h2 className="max-w-2xl font-serif text-5xl font-medium leading-[0.95] tracking-tighter md:text-7xl">
              Your favorites. <span className="italic text-sun">On repeat.</span>
            </h2>
          </Reveal>
          <Reveal delay={0.2}>
            <div className="max-w-md">
              <p className="text-base font-light leading-relaxed text-cream/80">
                Make wellness easier with The Squeeze Club — a simple way to keep
                your favorite Main Squeeze juices in rotation.
              </p>
              <p className="mt-4 text-base font-light leading-relaxed text-cream/80">
                Choose your favorite flavors, select a weekly or bi-weekly
                schedule, and we'll prepare your fresh cold-pressed bundle for
                pickup or delivery.
              </p>
              <p className="mt-4 font-cursive text-2xl text-sun">
                Freshly pressed. Ready when you are. Juiced with Love.
              </p>
            </div>
          </Reveal>
        </div>

        <div className="mt-16 grid grid-cols-1 gap-6 md:grid-cols-2">
          {PLANS.map((p, i) => (
            <Reveal key={p.id} delay={i * 0.12}>
              <article
                data-testid={`club-plan-${p.id}`}
                className="flex h-full flex-col rounded-[32px] bg-cream p-8 text-ink md:p-12"
              >
                <p className="font-cursive text-2xl text-melon">{p.tagline}</p>
                <h3 className="mt-3 font-serif text-4xl font-medium tracking-tight">{p.name}</h3>
                <p className="mt-4 font-serif text-6xl font-medium tracking-tighter md:text-7xl">
                  {p.price}
                  <span className="ml-2 align-middle font-sans text-sm font-light tracking-normal text-leaf/60">
                    {p.per}
                  </span>
                </p>
                <ul className="mt-8 flex-1 space-y-2.5">
                  {p.features.map((f) => (
                    <li key={f} className="flex items-center gap-3 text-sm font-light">
                      <Check className="h-4 w-4 shrink-0 text-melon" />
                      {f}
                    </li>
                  ))}
                </ul>
                <a
                  href={waLink(p.msg)}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={p.testid}
                  className="mt-8 inline-flex w-fit items-center gap-3 rounded-full bg-leaf px-8 py-4 text-sm font-medium uppercase tracking-[0.15em] text-cream transition-[background-color,transform] duration-300 hover:scale-95 hover:bg-melon"
                >
                  <MessageCircle className="h-4 w-4" />
                  {p.button}
                </a>
              </article>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.1}>
          <h3 className="mt-24 font-serif text-4xl font-medium italic tracking-tight md:text-5xl">
            How it works
          </h3>
        </Reveal>
        <div className="mt-10 grid grid-cols-1 gap-10 sm:grid-cols-2 lg:grid-cols-4">
          {STEPS.map(([title, text], i) => (
            <Reveal key={title} delay={i * 0.08}>
              <div data-testid={`club-step-${i + 1}`} className="border-t border-cream/20 pt-6">
                <p className="font-serif text-5xl font-medium text-sun">{String(i + 1).padStart(2, "0")}</p>
                <p className="mt-4 font-serif text-2xl font-medium tracking-tight">{title}</p>
                <p className="mt-2 text-sm font-light leading-relaxed text-cream/70">{text}</p>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.1}>
          <div className="mt-20 rounded-[32px] border border-cream/20 p-8 md:p-12">
            <h3 className="font-serif text-3xl font-medium italic tracking-tight md:text-4xl">
              Your squeeze, your way.
            </h3>
            <p className="mt-4 max-w-2xl text-base font-light leading-relaxed text-cream/80">
              Going away? Want different flavors next time? Need to skip an
              order? No problem. Contact us before your order cutoff to make
              changes to your upcoming bundle.
            </p>
            <p className="mt-6 text-xs font-light leading-relaxed tracking-wide text-cream/50">
              Premium juice selections may include a small additional charge.
              Pickup or $5 local delivery is available, subject to delivery
              availability for the customer's location.
            </p>
          </div>
        </Reveal>

        <div className="mt-20 border-t border-cream/15 pt-16">
          <Reveal>
            <SectionBadge light>Ready to join the club?</SectionBadge>
          </Reveal>
          <Reveal delay={0.1}>
            <h3 className="mt-8 max-w-3xl font-serif text-4xl font-medium leading-[0.95] tracking-tighter md:text-6xl">
              Make fresh cold-pressed juice part of your routine.
            </h3>
          </Reveal>
          <Reveal delay={0.2}>
            <a
              href={waLink("Hi Main Squeeze! I'm interested in joining The Squeeze Club. Please send me more information.")}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="join-club-button"
              className="mt-10 inline-flex items-center gap-3 rounded-full bg-cream px-8 py-4 text-sm font-medium uppercase tracking-[0.15em] text-leaf transition-[background-color,color,transform] duration-300 hover:scale-95 hover:bg-sun"
            >
              <MessageCircle className="h-4 w-4" />
              Join The Squeeze Club
            </a>
          </Reveal>
          <Reveal delay={0.3}>
            <p className="mt-12 font-cursive text-3xl text-sun">Juiced with Love</p>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
